import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Table, Input, InputNumber, Select, Switch, Tag, Tooltip, message, Button, Space } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  getServiceMappings,
  updateServiceMapping,
  getLogisticsProviders,
  getServices,
  getGateways,
} from '../../../services/logistics';
import type { ServiceMapping, LogisticsProvider, Service, Gateway } from '../../../services/logistics';

const PAGE_SIZE = 20;

const ServiceMappingListPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ServiceMapping[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [searchText, setSearchText] = useState('');
  // 默认只看未确认的——这个页面主要是给运营找"还没配置标准化"的记录用的
  const [onlyUnconfirmed, setOnlyUnconfirmed] = useState(false);
  // 默认按网关分组，方便一次性看完某个网关下所有待处理的原始记录，不用在列表里跳着找
  const [groupByGateway, setGroupByGateway] = useState(true);
  // 按网关筛选，undefined 表示不筛选（全部网关）
  const [gatewayFilter, setGatewayFilter] = useState<number | undefined>(undefined);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [gateways, setGateways] = useState<Gateway[]>([]);
  const [providers, setProviders] = useState<LogisticsProvider[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [optsLoading, setOptsLoading] = useState(false);
  // 每一行独立的"正在保存"状态，避免一行的请求把其它行也一起转圈
  const [savingIds, setSavingIds] = useState<Set<number>>(new Set());
  // 加价数值输入框的本地草稿值：按 mapping id 存，输入过程中不逐字符请求接口，失焦/回车才提交
  const [markupValueDrafts, setMarkupValueDrafts] = useState<Record<number, number | null>>({});
  // 加价方式的本地草稿值：切到"按比例"/"固定金额"这两种需要配合数值一起提交的方式时，
  // 先只更新这里、不立刻请求接口——真实遇到过直接提交 markup_type 但 markup_value 还是空的，
  // 后端校验"选择了加价方式必须填数值"直接报错，数值输入框因为依赖已保存的 markup_type
  // 才显示，请求一失败又打不开，等于卡死。"沿用网关默认"/"不加价"这两种不需要数值，仍然立刻提交。
  const [markupTypeDrafts, setMarkupTypeDrafts] = useState<Record<number, 'percentage' | 'fixed'>>({});

  const fetchOptions = async () => {
    setOptsLoading(true);
    try {
      const [gatewayResp, providerResp, serviceResp] = await Promise.all([
        getGateways({ page_size: 100 }),
        getLogisticsProviders({ page_size: 500 }),
        getServices({ page_size: 1000, is_active: true }),
      ]);
      const gatewayBody = gatewayResp.data as any;
      const providerBody = providerResp.data as any;
      const serviceBody = serviceResp.data as any;
      setGateways(Array.isArray(gatewayBody?.results) ? gatewayBody.results : (Array.isArray(gatewayBody) ? gatewayBody : []));
      setProviders(Array.isArray(providerBody?.results) ? providerBody.results : (Array.isArray(providerBody) ? providerBody : []));
      setServices(Array.isArray(serviceBody?.results) ? serviceBody.results : (Array.isArray(serviceBody) ? serviceBody : []));
    } catch {
      message.error('获取网关/承运商/产品目录失败');
    } finally {
      setOptsLoading(false);
    }
  };

  const fetchData = async (p: number, q?: string, unconfirmedOnly?: boolean, ps?: number, group?: boolean, gw?: number) => {
    const size = ps || pageSize;
    const grouped = group ?? groupByGateway;
    setLoading(true);
    try {
      const resp = await getServiceMappings({
        page: p,
        page_size: size,
        search: q || undefined,
        is_confirmed: unconfirmedOnly ? false : undefined,
        gateway: gw,
        // 按网关分组：先按网关名称排序，让同一个网关的记录连续排在一起，组内再按最近更新时间倒序；
        // 关闭分组时用回后端默认顺序（按创建时间倒序）
        ordering: grouped ? 'gateway_account__gateway__name,-updated_at' : undefined,
      });
      const body = resp.data as any;
      setData(Array.isArray(body?.results) ? body.results : (Array.isArray(body) ? body : []));
      setTotal(Number(body?.count ?? 0) || 0);
      setPage(p);
      setPageSize(size);
    } catch {
      message.error('获取标准化映射列表失败');
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOptions();
    fetchData(1, '', onlyUnconfirmed);
    return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchTextChange = (value: string) => {
    setSearchText(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => { fetchData(1, value, onlyUnconfirmed, undefined, undefined, gatewayFilter); }, 400);
  };

  const handleGatewayFilterChange = (value: number | undefined) => {
    setGatewayFilter(value);
    fetchData(1, searchText, onlyUnconfirmed, undefined, undefined, value);
  };

  const handleRefresh = async () => {
    // "刷新"重新拉取网关/承运商/物流产品目录（下拉选项用的），同时用当前的筛选条件重新拉一遍列表——
    // 这几个目录是页面打开时一次性加载进内存的，别的地方新增了网关/承运商/产品，这个页面不会自动感知
    await fetchOptions();
    await fetchData(1, searchText, onlyUnconfirmed, undefined, undefined, gatewayFilter);
    message.success('已刷新');
  };

  const handleToggleUnconfirmed = (checked: boolean) => {
    setOnlyUnconfirmed(checked);
    fetchData(1, searchText, checked, undefined, undefined, gatewayFilter);
  };

  const handleToggleGroup = (checked: boolean) => {
    setGroupByGateway(checked);
    fetchData(1, searchText, onlyUnconfirmed, undefined, checked, gatewayFilter);
  };

  // 本地原地更新一行，避免每次改完都整页重新拉取——重新拉取在"仅看未确认"模式下
  // 会让刚确认完的行立刻从列表里消失，运营看不到自己刚才操作的结果
  const patchLocalRow = (id: number, updated: Partial<ServiceMapping>) => {
    setData((prev) => prev.map((row) => (row.id === id ? { ...row, ...updated } : row)));
  };

  const withSaving = async (id: number, fn: () => Promise<void>) => {
    setSavingIds((prev) => new Set(prev).add(id));
    try {
      await fn();
    } finally {
      setSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleProviderChange = (record: ServiceMapping, providerId: number | undefined) => {
    withSaving(record.id, async () => {
      try {
        // 换了承运商，之前选的产品大概率不属于新承运商了，连带清空、取消确认，
        // 避免出现"承运商是A、产品却是B家的"这种不一致状态
        const resp = await updateServiceMapping(record.id, {
          logistics_provider: providerId ?? null,
          service: null,
          is_confirmed: false,
        });
        patchLocalRow(record.id, resp.data);
      } catch (e: any) {
        message.error(e?.response?.data?.detail || '更新失败');
      }
    });
  };

  const handleServiceChange = (record: ServiceMapping, serviceId: number | undefined) => {
    withSaving(record.id, async () => {
      try {
        const resp = await updateServiceMapping(record.id, { service: serviceId ?? null });
        patchLocalRow(record.id, resp.data);
      } catch (e: any) {
        message.error(e?.response?.data?.detail || '更新失败');
      }
    });
  };

  const handleConfirmChange = (record: ServiceMapping, checked: boolean) => {
    withSaving(record.id, async () => {
      try {
        const resp = await updateServiceMapping(record.id, { is_confirmed: checked });
        patchLocalRow(record.id, resp.data);
        // 仅看未确认模式下，确认完这一行应该从列表里消失
        if (checked && onlyUnconfirmed) {
          setData((prev) => prev.filter((row) => row.id !== record.id));
          setTotal((prev) => Math.max(0, prev - 1));
        }
      } catch (e: any) {
        message.error(e?.response?.data?.error || e?.response?.data?.detail || '更新失败');
      }
    });
  };

  const handleActiveChange = (record: ServiceMapping, checked: boolean) => {
    withSaving(record.id, async () => {
      try {
        const resp = await updateServiceMapping(record.id, { is_active: checked });
        patchLocalRow(record.id, resp.data);
      } catch (e: any) {
        message.error(e?.response?.data?.error || e?.response?.data?.detail || '更新失败');
      }
    });
  };

  const extractErrorMessage = (e: any): string => {
    const detail = e?.response?.data;
    const markupValueError = Array.isArray(detail?.markup_value) ? detail.markup_value[0] : detail?.markup_value;
    return markupValueError || detail?.error || detail?.detail || '更新失败';
  };

  const clearMarkupDrafts = (id: number) => {
    setMarkupValueDrafts((prev) => { const next = { ...prev }; delete next[id]; return next; });
    setMarkupTypeDrafts((prev) => { const next = { ...prev }; delete next[id]; return next; });
  };

  // Select 里"沿用网关默认"这个选项在 UI 上用 'inherit' 表示，对应后端的 markup_type=null。
  // "沿用网关默认"/"不加价"这两种不需要数值，选中后立刻提交；"按比例"/"固定金额"这两种
  // 必须配一个数值才能通过后端校验，这里先只记本地草稿、显示出数值输入框，
  // 等用户真的填了值（handleMarkupValueCommit）才一起提交，避免"选了方式但还没填数值"
  // 这个中间状态被立刻发给后端、必然校验失败。
  const handleMarkupTypeChange = (record: ServiceMapping, value: string) => {
    if (value === 'percentage' || value === 'fixed') {
      setMarkupTypeDrafts((prev) => ({ ...prev, [record.id]: value }));
      return;
    }
    const markup_type = value === 'inherit' ? null : (value as 'none');
    withSaving(record.id, async () => {
      try {
        const resp = await updateServiceMapping(record.id, { markup_type, markup_value: null });
        patchLocalRow(record.id, resp.data);
        clearMarkupDrafts(record.id);
      } catch (e: any) {
        message.error(extractErrorMessage(e));
      }
    });
  };

  const handleMarkupValueCommit = (record: ServiceMapping, markupType: 'percentage' | 'fixed', value: number | null) => {
    if (value == null) return; // 加价方式必须配数值，没填就不提交，草稿保留、输入框继续显示
    if (markupType === record.markup_type && value === (record.markup_value == null ? null : Number(record.markup_value))) {
      clearMarkupDrafts(record.id); // 方式和数值都没变，不用请求，但草稿要清掉，避免一直停留在草稿态
      return;
    }
    withSaving(record.id, async () => {
      try {
        const resp = await updateServiceMapping(record.id, { markup_type: markupType, markup_value: String(value) });
        patchLocalRow(record.id, resp.data);
      } catch (e: any) {
        message.error(extractErrorMessage(e));
      } finally {
        clearMarkupDrafts(record.id);
      }
    });
  };

  const columns: ColumnsType<ServiceMapping> = useMemo(() => ([
    {
      title: '物流网关',
      key: 'gateway',
      width: 120,
      fixed: 'left',
      render: (_: any, r: any) => (
        <div>
          <div>{r.gateway_name}</div>
          <div style={{ fontSize: 12, color: '#999' }}>{r.gateway_account_name}</div>
        </div>
      ),
    },
    { title: '原始承运商', dataIndex: 'external_carrier_code', width: 110, ellipsis: true },
    { title: '原始物流产品', dataIndex: 'external_service_code', width: 180, ellipsis: true },
    {
      title: '映射标准承运商',
      key: 'logistics_provider',
      width: 200,
      render: (_: any, r: ServiceMapping) => (
        <Select
          style={{ width: '100%' }}
          placeholder="选择承运商"
          allowClear
          showSearch
          loading={optsLoading}
          disabled={savingIds.has(r.id)}
          value={r.logistics_provider ?? undefined}
          options={providers.map((p) => ({ value: p.id, label: `${p.name} (${p.code})` }))}
          optionFilterProp="label"
          filterOption={(input, opt) => String(opt?.label || '').toLowerCase().includes(input.toLowerCase())}
          onChange={(v) => handleProviderChange(r, v)}
        />
      ),
    },
    {
      title: '映射标准物流产品',
      key: 'service',
      width: 400,
      render: (_: any, r: ServiceMapping) => {
        const options = services
          .filter((s) => s.logistics_provider === r.logistics_provider)
          .map((s) => ({ value: s.id, label: `${s.name} (${s.code})` }));
        const selectedLabel = options.find((o) => o.value === r.service)?.label;
        const selectNode = (
          <Select
            style={{ width: '100%' }}
            placeholder={r.logistics_provider ? '选择具体产品' : '请先选承运商'}
            allowClear
            showSearch
            loading={optsLoading}
            disabled={savingIds.has(r.id) || !r.logistics_provider}
            value={r.service ?? undefined}
            options={options}
            optionFilterProp="label"
            filterOption={(input, opt) => String(opt?.label || '').toLowerCase().includes(input.toLowerCase())}
            onChange={(v) => handleServiceChange(r, v)}
          />
        );
        // 列宽有限，选中的产品名称长的话框里会截断；hover 整个下拉框时用 Tooltip 补全显示完整值
        return selectedLabel ? <Tooltip title={selectedLabel}>{selectNode}</Tooltip> : selectNode;
      },
    },
    {
      title: '映射确认',
      key: 'is_confirmed',
      width: 110,
      render: (_: any, r: ServiceMapping) => {
        const canConfirm = !!(r.logistics_provider && r.service);
        const switchNode = (
          <Switch
            checked={r.is_confirmed}
            disabled={savingIds.has(r.id) || (!r.is_confirmed && !canConfirm)}
            onChange={(checked) => handleConfirmChange(r, checked)}
          />
        );
        return canConfirm || r.is_confirmed ? switchNode : (
          <Tooltip title="请先选择承运商和具体产品">{switchNode}</Tooltip>
        );
      },
    },
    {
      title: '启用此物流产品',
      key: 'is_active',
      width: 120,
      render: (_: any, r: ServiceMapping) => (
        <Tooltip title="只关闭这一个网关报出的这一个具体报价，不影响同一个产品在其它网关下的报价">
          <Switch
            checked={r.is_active}
            disabled={savingIds.has(r.id)}
            onChange={(checked) => handleActiveChange(r, checked)}
          />
        </Tooltip>
      ),
    },
    {
      title: '加价策略（覆盖网关默认）',
      key: 'markup',
      width: 260,
      render: (_: any, r: ServiceMapping) => {
        // 方式的显示值：本地草稿（刚选了"按比例"/"固定金额"、还没填数值）优先于已保存的值
        const markupType = markupTypeDrafts[r.id] ?? r.markup_type ?? 'inherit';
        const draft = markupValueDrafts[r.id];
        const currentValue = draft !== undefined ? draft : (r.markup_value == null ? null : Number(r.markup_value));
        return (
          <Space size={4}>
            <Select
              style={{ width: 140 }}
              disabled={savingIds.has(r.id)}
              value={markupType}
              onChange={(v) => handleMarkupTypeChange(r, v)}
              options={[
                { value: 'inherit', label: '沿用网关默认' },
                { value: 'none', label: '不加价' },
                { value: 'percentage', label: '按比例加价' },
                { value: 'fixed', label: '固定金额加价' },
              ]}
            />
            {(markupType === 'percentage' || markupType === 'fixed') && (
              <InputNumber
                style={{ width: 100 }}
                precision={2}
                disabled={savingIds.has(r.id)}
                placeholder={markupType === 'percentage' ? '百分比' : '金额'}
                value={currentValue}
                onChange={(v) => setMarkupValueDrafts((prev) => ({ ...prev, [r.id]: v }))}
                onBlur={() => handleMarkupValueCommit(r, markupType, currentValue)}
                onPressEnter={() => handleMarkupValueCommit(r, markupType, currentValue)}
              />
            )}
          </Space>
        );
      },
    },
    {
      title: '是否生效',
      dataIndex: 'is_mapped',
      width: 90,
      render: (v: boolean) => (v ? <Tag color="green">生效中</Tag> : <Tag color="default">未标准化</Tag>),
    },
  ]), [providers, services, optsLoading, savingIds, onlyUnconfirmed, markupValueDrafts, markupTypeDrafts]);

  return (
    <div>
      <div style={{ marginBottom: 12, color: '#999' }}>
        网关返回的原始承运商/服务名称各不相同，这里把它们关联到统一的标准承运商和具体产品；
        确认后，报价时同一个标准产品在多个网关返回的报价中只会展示价格最低的一条。
        "加价策略"可以精细到只覆盖某一个网关报出的某一个具体物流产品的加价方式（留空则沿用所在网关的默认策略）；
        "启用"开关可以精细到只关闭某一个网关报出的某一个具体物流产品——两者都不影响同一个产品在其它网关下的报价，
        也无论此产品是否已经标准化。共 {total} 条记录。
      </div>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        <Select
          placeholder="全部网关"
          style={{ width: 180 }}
          allowClear
          loading={optsLoading}
          value={gatewayFilter}
          options={gateways.map((g) => ({ value: g.id, label: g.name }))}
          onChange={(v) => handleGatewayFilterChange(v ?? undefined)}
        />
        <Input
          placeholder="搜索原始承运商/服务名称"
          prefix={<SearchOutlined />}
          style={{ width: 260 }}
          value={searchText}
          onChange={(e) => handleSearchTextChange(e.target.value)}
          allowClear
        />
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Switch checked={onlyUnconfirmed} onChange={handleToggleUnconfirmed} />
          <span>仅看未映射确认</span>
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Switch checked={groupByGateway} onChange={handleToggleGroup} />
          <span>按网关分组</span>
        </span>
        <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={optsLoading || loading}>刷新</Button>
      </div>

      <Table<ServiceMapping>
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        onRow={(record) => (record.is_confirmed ? {} : { style: { backgroundColor: '#ffa39e' } })}
        onChange={(p) => fetchData(Number(p.current || 1), searchText, onlyUnconfirmed, Number(p.pageSize || pageSize), groupByGateway, gatewayFilter)}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (t, range) => `共${t}条记录，显示${range[0]}-${range[1]}条`,
        } as TablePaginationConfig}
        scroll={{ x: 1460 }}
      />
    </div>
  );
};

export default ServiceMappingListPage;
